/**
 * The provenance half of screen 3, wired to the SIGNED engine.
 *
 * `_OutputProvenance.tsx` renders the PREVIEW result (`RunResult` from
 * `POST .../run`). This file renders `POST .../recompute`'s response
 * (`RecomputeResponse`) instead — a materially narrower wire shape, built
 * under VAL-INDUSTRY-WIRING-1 R6A. Wiring this in to replace the preview
 * panels on `ValuationOutput.tsx` is a later session's job (FENCE); nothing
 * imports this file yet.
 *
 * Same transcription-only rule as the file this will replace: nothing here
 * recomputes, re-weights or re-rounds an engine number. `income_leg`,
 * `triangulation` and `asset_floor` are each nullable on the wire — a null
 * block renders "Not stated", never a guessed number.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { RecomputeResponse } from '@/types/valuationB3'
import { Fact, basisLabel } from './_shared'
import { convergenceLabel } from './_convergenceLabel'
import { humanizeDriver } from './_IntakeDrivers'
import { fracPct, money, multiple, pct } from '@/utils/format'

// WALKFIX-A F11 — `owner` hides internal engine notes (ticket ids, "PENDING",
// snake_case keys) from an owner; staff still see the record's full text.
type PanelProps = { run: RecomputeResponse; owner?: boolean }

// WF-A-1: owner-approved, verbatim (OWNER_APPROVALS.log 2026-10-25 11:15 CT).
const SIZE_PREMIUM_SUBSTITUTED = 'Size premium: the standard figure for small companies (Kroll, 2024).'

export function BenchmarkPanel({ run, owner = false }: PanelProps) {
  const c = run.computed_range
  return (
    <Card>
      <CardHeader><CardTitle>Benchmark</CardTitle></CardHeader>
      <CardContent>
        <Fact label="Benchmark" value={owner ? c.benchmark_label : `${c.benchmark_label} (${c.benchmark_key})`} />
        <Fact label="Basis" value={basisLabel(c.basis)} />
        <Fact label="Range" value={`${multiple(c.low)} – ${multiple(c.high)}`} />
        <Fact label="Normalized earnings" value={money(c.normalized_earnings)} />
        <Fact
          label="Provisional"
          value={c.provisional ? 'Not yet co-signed by a valuation signer' : 'Co-signed by a valuation signer'}
        />
        <Fact label="Source" value={c.source_label ?? c.source} />
        <Fact label="Refreshed" value={c.refreshed_on} />
        <p className="mt-3 text-xs text-pal-ink-muted">{c.disclosure}</p>
      </CardContent>
    </Card>
  )
}

export function EarningsAndRatePanel({ run, owner = false }: PanelProps) {
  const e = run.income_leg
  return (
    <Card>
      <CardHeader><CardTitle>Earnings basis &amp; build-up rate</CardTitle></CardHeader>
      <CardContent>
        {!e ? (
          <p className="text-sm text-pal-ink-muted">Not stated</p>
        ) : (
          <>
            <Fact
              label="Income basis"
              value={e.income_basis_is_sub_crossover ? 'Below crossover' : 'At/above crossover'}
            />
            <Fact label="Crossover threshold" value={money(e.crossover_earnings_threshold)} />
            <Fact label="Risk-free rate" value={pct(e.risk_free_rate)} />
            <Fact label="Equity risk premium" value={pct(e.equity_risk_premium)} />
            <Fact label="Size premium" value={pct(e.size_premium)} />
            {!owner ? (
              <p className="mb-2 text-xs text-pal-ink-muted">{e.size_premium_source}</p>
            ) : e.size_premium_source !== 'caller-supplied' && (
              <p className="mb-2 text-xs text-pal-ink-muted">{SIZE_PREMIUM_SUBSTITUTED}</p>
            )}
            <Fact label="Company-specific risk" value={pct(e.company_specific_risk)} />
            <Fact label="Discount rate" value={pct(e.discount_rate)} />
            <Fact label="Long-term growth" value={pct(e.long_term_growth_rate)} />
            <Fact label="Capitalization rate" value={pct(e.capitalization_rate)} />
            <p className="mt-3 text-xs text-pal-ink-muted">{e.rate_source_note}</p>
            <Fact label="Capitalized earnings value" value={money(e.capitalized_earnings_value)} />
            <Fact label="DCF value" value={money(e.dcf_value)} />
            <Fact
              label="Weighting"
              value={`${fracPct(e.capitalized_earnings_weight)} cap / ${fracPct(e.dcf_weight)} DCF`}
            />
            <Fact label="Income value" value={money(e.income_value)} />
            {typeof e.basis_disclosure?.note === 'string' ? (
              <p className="mt-3 text-xs text-pal-ink-muted">{e.basis_disclosure.note}</p>
            ) : typeof e.basis_disclosure?.replacement_compensation === 'number' ? (
              <>
                <Fact label="Owner replacement wage" value={money(e.basis_disclosure.replacement_compensation)} />
                <Fact label="Deducted from earnings of" value={money(e.basis_disclosure.deducted_from)} />
                <Fact label="Earnings capitalized" value={money(e.basis_disclosure.income_numerator)} />
              </>
            ) : (
              <p className="text-sm text-pal-ink-muted">Not stated</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function ApproachesPanel({ run, owner = false }: PanelProps) {
  const lines = run.questionnaire.lines
  const a = run.asset_floor
  return (
    <Card>
      <CardHeader><CardTitle>Approaches</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="mb-1 text-xs font-medium text-pal-ink-muted">Market</p>
          <Fact label="Market value" value={money(run.market_value)} />
        </div>

        {lines.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium text-pal-ink-muted">Value drivers</p>
            {lines.map((l, i) => (
              <Fact key={i} label={humanizeDriver(l.driver)} value={`${l.score} / ${l.max_score}`} />
            ))}
          </div>
        )}

        <div>
          <p className="mb-1 text-xs font-medium text-pal-ink-muted">Asset floor</p>
          {!a ? (
            <p className="text-sm text-pal-ink-muted">Not stated</p>
          ) : (
            <>
              <Fact label="Total assets" value={money(a.total_assets)} />
              <Fact label="Total liabilities" value={money(a.total_liabilities)} />
              <Fact label="Fair-value adjustments" value={money(a.fair_value_adjustments)} />
              <Fact label="Floor value" value={money(a.value)} />
              {!owner && <p className="mt-1 text-xs text-pal-ink-muted">{a.source_note}</p>}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function TriangulationPanel({ run }: { run: RecomputeResponse }) {
  const t = run.triangulation
  const r = run.reconciliation_check
  return (
    <Card>
      <CardHeader><CardTitle>Triangulation</CardTitle></CardHeader>
      <CardContent>
        {!t ? (
          <p className="text-sm text-pal-ink-muted">Not stated</p>
        ) : (
          <>
            <Fact label="Income leg" value={`${money(t.income_value)} @ ${fracPct(t.weights.income_weight)}`} />
            <Fact label="Market leg" value={`${money(t.market_value)} @ ${fracPct(t.weights.market_weight)}`} />
            {t.dcf_cross_check_value !== null && (
              <Fact
                label="DCF cross-check"
                value={`${money(t.dcf_cross_check_value)} @ ${fracPct(t.weights.dcf_cross_check_weight)}`}
              />
            )}
            <Fact label="Blended value" value={money(t.blended_value)} />
            <Fact label="Floor applied" value={t.floor_applied ? 'Yes' : 'No'} />
            <Fact label="Point estimate" value={money(t.point_estimate)} />
            <Fact label="Range" value={`${money(t.low)} – ${money(t.high)}`} />
            <Fact label="Spread" value={pct(t.spread_pct, 1)} />
            <Fact label="Convergence" value={convergenceLabel(t.convergence_status)} mono />
            {r && (
              <>
                <Fact label="Implied income multiple" value={multiple(r.implied_multiple)} />
                <p
                  className={`mt-1 text-xs ${
                    r.inside_signed_range ? 'text-gray-500' : 'font-medium text-amber-600'
                  }`}
                >
                  {r.note}
                </p>
              </>
            )}
            <p className="mt-3 text-xs text-pal-ink-muted">{t.source_note}</p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * VAL-INDUSTRY-WIRING-1 R6D — the valuator's comparable sales, shown as
 * evidence next to the signed number. Transcription only: this panel never
 * renders a number this file computed, and the comps it shows never changed
 * the signed value (see the FENCE in `VAL-INDUSTRY-WIRING-1-R6D.md`). To use
 * a different multiple the valuator uses Override, on `OverridesPanel` above.
 */
export function ComparableEvidencePanel({ run }: { run: RecomputeResponse }) {
  const ce = run.comparable_evidence
  return (
    <Card>
      <CardHeader><CardTitle>Comparable sales (evidence)</CardTitle></CardHeader>
      <CardContent>
        {!ce ? (
          <p className="text-sm text-pal-ink-muted">Not stated</p>
        ) : (
          <>
            <Fact label="Comparable sales" value={`${ce.comp_count} supplied`} />
            <Fact label="Low multiple" value={`${multiple(ce.low_multiple)} (${money(ce.low_value)})`} />
            <Fact label="Median multiple" value={`${multiple(ce.median_multiple)} (${money(ce.median_value)})`} />
            <Fact label="High multiple" value={`${multiple(ce.high_multiple)} (${money(ce.high_value)})`} />
            <Fact label="Signed range" value={`${multiple(ce.signed_low)} – ${multiple(ce.signed_high)}`} />
            {ce.outside_signed_range && (
              <p className="mt-2 text-sm font-medium text-amber-600">{ce.note}</p>
            )}
            <p className="mt-3 text-xs text-pal-ink-muted">{ce.source_note}</p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function OverridesPanel({ run }: { run: RecomputeResponse }) {
  const p = run.positioned
  return (
    <Card>
      <CardHeader><CardTitle>Overrides</CardTitle></CardHeader>
      <CardContent>
        <Fact label="Concluded multiple" value={multiple(run.concluded_multiple)} />
        <Fact label="Multiple source" value={run.multiple_source_label ?? run.multiple_source} />
        <Fact label="Positioned multiple" value={multiple(p.multiple)} />
        <Fact label="Position" value={`${p.position_ordinal} (${pct(p.position_pct)})`} />
        <p className="mt-3 text-xs text-pal-ink-muted">{p.disclosure}</p>
        <Fact label="Clamp note" value={run.clamp_note ?? 'None'} />
        <Fact label="Override out of range" value={run.override_out_of_range ? 'Yes' : 'No'} />
        <Fact
          label="Override unresolved"
          value={run.override_unresolved ? 'Yes — blocks finalize' : 'No'}
        />
      </CardContent>
    </Card>
  )
}
